import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { AUTH_CONFIG_IDS } from 'app/auth/auth.config';
import { getInstallerAccessToken } from 'app/auth/getInstallerAccessToken';

import { DEFAULT_LOCALE } from '../i18n/i18n.config';
import { APP_CONFIG } from '../injectionTokens';
import { AppConfig } from '../models/AppConfig';

import { SecurityService } from './SecurityService';

type QueryParams = Record<string, string | number | boolean | ReadonlyArray<string | number | boolean>>;

@Injectable()
export class HttpService {
    protected baseUrl: string;

    constructor(
        private http: HttpClient,
        private translateService: TranslateService,
        private router: Router,
        private securityService: SecurityService,
        @Inject(APP_CONFIG) private appConfig: AppConfig,
    ) {
        this.baseUrl = this.appConfig.servegoApi.url;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    get(url: string, queryParams?: QueryParams): Observable<any> {
        const httpOptions = this.getHttpOptions(queryParams);
        return this.http.get(this.baseUrl + url, httpOptions).pipe(catchError(this.handleError()));
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    post(url: string, post: any, queryParams?: QueryParams): Observable<any> {
        const httpOptions = this.getHttpOptions(queryParams);
        return this.http.post(this.baseUrl + url, post, httpOptions).pipe(catchError(this.handleError()));
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    put(url: string, putData: any, queryParams?: QueryParams): Observable<any> {
        const httpOptions = this.getHttpOptions(queryParams);
        return this.http.put(this.baseUrl + url, putData, httpOptions).pipe(catchError(this.handleError()));
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    patch(url: string, post: any, queryParams?: QueryParams): Observable<any> {
        const httpOptions = this.getHttpOptions(queryParams);
        return this.http.patch(this.baseUrl + url, post, httpOptions).pipe(catchError(this.handleError()));
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    delete(url: string, queryParams?: QueryParams): Observable<any> {
        const httpOptions = this.getHttpOptions(queryParams);
        return this.http.delete(this.baseUrl + url, httpOptions).pipe(catchError(this.handleError()));
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    getDownload(url: string): Observable<any> {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const httpOptions: any = this.getHttpOptions();
        httpOptions.headers.set('Accept', 'application/pdf');
        httpOptions.responseType = 'blob';
        return this.http.get(this.baseUrl + url, httpOptions).pipe(catchError(this.handleError()));
    }

    private getHttpOptions(queryParams: QueryParams = {}): { headers: HttpHeaders; params: QueryParams } {
        const headers = {
            Authorization: `Bearer ${getInstallerAccessToken()}`,
            'Accept-Language': this.translateService.getCurrentLang() || DEFAULT_LOCALE,
            Timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        };

        return {
            headers: new HttpHeaders(headers),
            params: queryParams,
        };
    }

    private handleError() {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return (error: any): Observable<never> => {
            // TODO: send the error to remote logging infrastructure
            switch (error.status) {
                case 401:
                    this.securityService.logout(AUTH_CONFIG_IDS.INSTALLER);
                    break;
                case 403:
                    this.router.navigate(['/unauthorized']);
                    break;
                default:
                    console.warn(error.message);
            }
            // Let the app keep running by returning an empty result.
            throw error;
        };
    }
}
